import React, { useState } from "react";
import { useExtractColors } from "react-extract-colors";
import { useTextColorByImage } from "../utils/getTextColorByImage";

import PlayIcon from "../assets/icons/bold/Play.svg";
import PauseIcon from "../assets/icons/bold/Pause.svg";

import { FaBackward, FaForward, FaVolumeUp, FaPlay, FaCar, FaClock } from "react-icons/fa";
import WaveformSlider from "../components/WaveformSlider";
import Spinner from "../components/Spinner";

import DetailHeader from "../components/DetailHeader";

export default function MiniPlayerUI({
    expanded, toggleExpand, handleTouchStart, handleTouchMove, handleTouchEnd,
    progress, bookInfo, audioList, currentIndex, handlePlayPause, handlePlay, isPlaying,
    formatTime, currentTime, handleSeek, duration, handlePrev, handleNext,
    setCurrentTime, audioRef, currentPlayingIndex, isSongTab, bookId
}) {
    const { dominantColor, lighterColor } = useExtractColors(bookInfo?.imageUrl || "");
    const { textColor, isDark } = useTextColorByImage(bookInfo?.imageUrl || "");
    const [showAll, setShowAll] = useState(false);
    const maxToShow = 3;
    const visibleItems = showAll ? audioList : audioList.slice(0, maxToShow);

    const IconButton = ({ children, onClick }) => (
        <button onClick={onClick} className="p-2 rounded-full transition-all" style={{ color: textColor }}>
            {children}
        </button>
    );
    const currentAudioLink = audioList[currentPlayingIndex]?.url;
    const currentAudioName = audioList[currentPlayingIndex]?.name;

    return (
        <div
            className={`w-full transition-all duration-300 ${expanded ? "h-full z-50" : "h-20 z-10"} flex flex-col`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={!expanded ? toggleExpand : undefined}
            style={{ backgroundColor: expanded ? lighterColor : "transparent", color: textColor }}
        >
            {!expanded ? (
                <>
                    {/* Mini Player UI */}
                    <div className="w-full h-1 bg-gray-300 rounded-full">
                        <div className="h-1 bg-blue-500 rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="flex items-center w-full px-4 py-2 mb-4">
                        <img src={bookInfo.imageUrl} alt="Cover" className="w-12 h-12 rounded-lg mr-3" />
                        <div className="flex-1">
                            <span className="text-sm font-medium">
                                {audioList[currentIndex]?.name || <Spinner />}
                            </span>
                            {bookInfo.author && (
                                <p className="text-xs opacity-70">{bookInfo.author}</p>
                            )}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handlePlayPause(); }} className="p-2">
                            <img src={isPlaying ? PauseIcon : PlayIcon} alt="Play/Pause" className="w-8 h-8" />
                        </button>
                    </div>
                </>
            ) : (
                <div className="flex flex-col w-full h-full">
                    {/* Expanded Player UI - Header */}
                    {/* Replaced your custom header with DetailHeader */}
                    <DetailHeader
                        title={audioList[currentPlayingIndex]?.name || bookInfo.title}
                        onBack={toggleExpand} // Use toggleExpand to go back to mini player
                        textColor={textColor}
                        bgColor={dominantColor}
                        bookDescription={bookInfo.description}
                        bookAuthor={bookInfo.author}
                        currentAudioLink={currentAudioLink}
                        currentAudioName={currentAudioName}
                        bookId={bookId}
                    />

                    {/* Content */}
                    <div className="flex-1 flex flex-col items-center overflow-y-auto px-4 py-4">
                        {isSongTab ? (
                            <div className="relative flex items-center justify-center mb-6 w-52 h-52">
                                <div className={`w-44 h-44 rounded-full border-4 border-gray-300 shadow-lg overflow-hidden
                                    ${isPlaying ? "animate-spin-slow" : ""}`}>
                                    <img
                                        src={bookInfo.imageUrl}
                                        alt="Disc"
                                        className="w-full h-full object-cover rounded-full"
                                    />
                                </div>

                                {/* Gramophone tonearm */}
                                <div className="absolute right-0 top-6 w-16 h-16">
                                    <div className="w-1 h-10 bg-gray-700 rounded-sm rotate-[45deg] origin-top-left"></div>
                                    <div className="w-2 h-4 bg-gray-600 mt-1 ml-2 rounded-full"></div>
                                </div>
                            </div>
                        ) : (
                            <img
                                src={bookInfo.imageUrl}
                                alt="Cover"
                                className="w-44 rounded-2xl mb-6 shadow-lg object-cover border-2 border-opacity-30"
                                style={{ borderColor: textColor }}
                            />
                        )}
                        <div className="w-full space-y-2 max-h-[300px] overflow-y-auto">
                            {visibleItems.map((item, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center justify-between p-2 rounded-lg border transition-colors duration-300 ${currentPlayingIndex === index
                                        ? 'bg-opacity-20 border-opacity-60'
                                        : 'border-gray-300'
                                        }`}
                                    style={{
                                        backgroundColor: currentPlayingIndex === index ? dominantColor : "transparent",
                                        borderColor: currentPlayingIndex === index ? dominantColor : "#ccc",
                                        color: currentPlayingIndex === index ? textColor : "inherit"
                                    }}
                                >
                                    <span className="text-sm">{item.name}</span>
                                    <button
                                        onClick={() => handlePlay(index)}
                                        className="text-xs p-2 rounded-full hover:bg-gray-200 transition"
                                        style={{ color: textColor }}
                                    >
                                        {currentPlayingIndex === index ? (
                                            <FaVolumeUp size={16} />
                                        ) : (
                                            <FaPlay size={16} />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                        {audioList.length > maxToShow && (
                            <button
                                onClick={() => setShowAll(!showAll)}
                                className="mt-4 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                                style={{
                                    backgroundColor: dominantColor,
                                    color: isDark ? "#fff" : "#000",
                                }}
                            >
                                {showAll ? "Жасыру" : "Көбірек көрсету"}
                            </button>
                        )}
                    </div>

                    {/* Controls */}
                    <div className="flex flex-col items-center w-full px-4 mb-16">
                        <div className="flex items-center justify-between w-full mb-2">
                            <span className="text-xs opacity-70">{formatTime(currentTime)}</span>
                            <WaveformSlider
                                currentTime={currentTime}
                                duration={duration}
                                setCurrentTime={setCurrentTime}
                                audioRef={audioRef}
                                isPlaying={isPlaying}
                            />
                            <span className="text-xs opacity-70">{formatTime(duration)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <button className="px-4 py-2 bg-opacity-20 rounded-lg text-sm" style={{ backgroundColor: dominantColor, color: textColor }}>
                                <FaCar size={16} className="mr-2" />
                            </button>
                            <IconButton onClick={handlePrev}><FaBackward size={24} /></IconButton>
                            <button onClick={handlePlayPause}>
                                <img src={isPlaying ? PauseIcon : PlayIcon} alt="Play/Pause" className="w-12 h-12" />
                            </button>
                            <IconButton onClick={handleNext}><FaForward size={24} /></IconButton>
                            <button className="px-4 py-2 bg-opacity-20 rounded-lg text-sm" style={{ backgroundColor: dominantColor, color: textColor }}>
                                <FaClock size={16} className="mr-2" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}